<template>
    <div class="rt-panel card">
        <div v-if="loading && !detail" class="rt-panel-empty">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
        </div>

        <template v-else-if="detail">
            <!-- Cabecera de la mesa -->
            <div class="rt-panel-head">
                <div>
                    <h3 class="m-0">{{ table.name }}</h3>
                    <span v-if="table.zone" class="rt-panel-sub">{{ table.zone }}</span>
                </div>
                <div class="rt-head-right">
                    <span class="rt-state-chip" :class="'rt-state-' + table.state">
                        {{ stateLabel(table.state) }}
                        <template v-if="table.capacity"> · {{ table.capacity }} pers</template>
                    </span>
                    <Button icon="pi pi-times" class="p-button-text p-button-rounded p-button-sm"
                            aria-label="Cerrar panel de la mesa" @click="$emit('close')" />
                </div>
            </div>

            <!-- Resumen de la mesa (suma de cuentas) -->
            <div class="rt-summary">
                <div><span class="rt-sum-label">Total mesa</span><span class="rt-sum-value">${{ money(table.total) }}</span></div>
                <div><span class="rt-sum-label">Pagado</span><span class="rt-sum-value">${{ money(table.paid) }}</span></div>
                <div><span class="rt-sum-label">Saldo</span><span class="rt-sum-value rt-pending">${{ money(table.pending) }}</span></div>
            </div>

            <!-- Pestañas de cuentas -->
            <div class="rt-accounts">
                <button v-for="acc in accounts" :key="acc.id" class="rt-acc-tab"
                        :class="{ 'rt-acc-active': acc.id === selectedAccountId }" @click="selectedAccountId = acc.id">
                    <span class="rt-acc-name">{{ acc.accountLabel }}</span>
                    <span class="rt-acc-money">${{ money(acc.total) }} · saldo ${{ money(acc.pending) }}</span>
                </button>
                <Button label="Nueva cuenta" icon="pi pi-plus" class="p-button-outlined p-button-sm rt-new-acc"
                        @click="$emit('open-account')" />
            </div>

            <div v-if="!accounts.length" class="rt-panel-empty">
                <p>Esta mesa está libre.</p>
                <Button label="Abrir cuenta" icon="pi pi-plus" @click="$emit('open-account')" />
            </div>

            <template v-else-if="account">
                <!-- Ítems de la cuenta -->
                <div class="rt-items">
                    <div v-for="it in account.items" :key="it.id" class="rt-item-row">
                        <span class="rt-item-qty">{{ it.amount }}</span>
                        <!-- La comanda sale sola al agregar el ítem y no se muestra su estado (como en el resto de Contablus) -->
                        <span class="rt-item-name">{{ it.name }}</span>
                        <span class="rt-item-total">${{ money(it.total) }}</span>
                        <Button icon="pi pi-times" class="p-button-rounded p-button-text p-button-sm rt-item-del"
                                @click="removeItem(it)" />
                    </div>
                    <div v-if="!account.items.length" class="rt-empty-sm">Sin ítems. Agrega con «+ Ítems».</div>
                </div>

                <div class="rt-totals">
                    <div><span>Subtotal</span><span>${{ money(account.subtotal) }}</span></div>
                    <div><span>IVA</span><span>${{ money(account.tax) }}</span></div>
                    <div class="rt-grand"><span>Total {{ account.accountLabel }}</span><span>${{ money(account.total) }}</span></div>
                </div>

                <!-- Acciones del pedido -->
                <div class="rt-actions">
                    <Button v-if="kitchenEnabled" label="Cocina" icon="pi pi-send" class="p-button-warning" :loading="kitchenBusy"
                            @click="sendToKitchen" />
                    <Button label="Ítems" icon="pi pi-plus" class="p-button-outlined" @click="$emit('add-items', account)" />
                    <Button label="Mover" icon="pi pi-arrow-right" class="p-button-outlined"
                            @click="moveVisible = true" />
                    <Button label="Nombre" icon="pi pi-pencil" class="p-button-outlined" @click="openRename" />
                </div>

                <!-- Cobro: primero la decisión; el detalle solo aparece según la vía elegida -->
                <div v-if="account.items.length" class="rt-pay" :class="'rt-stage-' + payStage">
                    <!-- Sin permiso de cobrar (contabilidad automática y sin «crear movimientos»): la cobra caja -->
                    <div v-if="!setup.canCollect && payStage !== 'done'" class="rt-pay-locked">
                        <i class="pi pi-lock"></i>
                        <span>Por cobrar <b>${{ money(account.pending) }}</b>. Esta cuenta la cobra caja.</span>
                    </div>

                    <!-- 1. Elegir cómo cobrar -->
                    <template v-else-if="payStage === 'choose'">
                        <div class="rt-pay-head">
                            <span>Por cobrar</span>
                            <span class="rt-pay-amount">${{ money(account.pending) }}</span>
                        </div>
                        <div class="rt-pay-choices">
                            <Button label="Cobrar" icon="pi pi-wallet" @click="payMode = 'full'" />
                            <Button v-if="splitPayment" label="Pago múltiple" icon="pi pi-users" class="p-button-outlined"
                                    @click="payMode = 'split'" />
                        </div>
                    </template>

                    <!-- 2a. Cobrar: un pago por lo que falta + facturar o archivar -->
                    <template v-else-if="payStage === 'full'">
                        <div class="rt-pay-head">
                            <span>Cobrar</span>
                            <Button label="Volver" class="p-button-text p-button-sm" @click="payMode = null" />
                        </div>
                        <PaymentEntryForm ref="payForm" mode="full" :due="Number(account.pending)" :setup="setup"
                                          :saving="paying" :serverError="payError" @submit="onPaymentSubmit">
                            <InvoiceBuyerForm v-model="buyer" :total="Number(account.total)" :limit="finalLimit"
                                              :errorField="buyerErrorField" />
                        </PaymentEntryForm>
                    </template>

                    <!-- 2b. Pago múltiple: cada persona paga lo suyo (con su propina); el último cobra y cierra -->
                    <template v-else-if="payStage === 'split'">
                        <div class="rt-pay-head">
                            <span>Pago múltiple</span>
                            <Button v-if="!account.payments.length" label="Volver" class="p-button-text p-button-sm"
                                    @click="payMode = null" />
                        </div>
                        <div class="rt-progress" role="progressbar" :aria-valuenow="paidPercent" aria-valuemin="0"
                             aria-valuemax="100" :aria-label="'Pagado ' + paidPercent + '%'">
                            <span :style="{ width: paidPercent + '%' }"></span>
                        </div>
                        <div class="rt-progress-legend">
                            <span>Pagado ${{ money(account.paid) }} de ${{ money(account.total) }}</span>
                            <b>Faltan ${{ money(account.pending) }}</b>
                        </div>
                        <PayerList :payments="account.payments" />
                        <!-- El pago que completa lo que falta cobra, cierra y factura: trae los datos de factura -->
                        <PaymentEntryForm ref="payForm" mode="split" :due="Number(account.pending)" :setup="setup"
                                          :saving="paying" :serverError="payError" @submit="onPaymentSubmit">
                            <InvoiceBuyerForm v-model="buyer" :total="Number(account.total)" :limit="finalLimit"
                                              :errorField="buyerErrorField" />
                        </PaymentEntryForm>
                    </template>

                    <!-- 3. Cubierto: datos de factura y facturar o archivar -->
                    <template v-else>
                        <div class="rt-pay-head rt-pay-done">
                            <span><i class="pi pi-check-circle"></i> Pagado completo</span>
                            <span class="rt-pay-amount">${{ money(account.total) }}</span>
                        </div>
                        <PayerList :payments="account.payments" />
                        <InvoiceBuyerForm v-model="buyer" :total="Number(account.total)" :limit="finalLimit"
                                          :errorField="buyerErrorField" />
                        <Button :label="'Facturar ' + account.accountLabel" icon="pi pi-receipt" class="rt-pay-primary"
                                :loading="collecting" @click="collect('invoice')" />
                        <Button label="Archivar" class="p-button-text p-button-sm rt-pay-secondary"
                                :disabled="collecting" @click="confirmArchive()" />
                    </template>
                </div>
            </template>
        </template>

        <div v-else class="rt-panel-empty rt-placeholder">
            <i class="pi pi-table" style="font-size: 2rem;"></i>
            <p>Elige una mesa del salón para ver sus cuentas.</p>
        </div>

        <!-- Mover cuenta a otra mesa: se elige en el mapa, igual que en el salón -->
        <Dialog v-model:visible="moveVisible" :modal="true" :header="moveHeader" :style="{width: '720px'}"
                :breakpoints="{'800px': '96vw'}">
            <div v-if="moveTabs.length > 1" class="rt-move-zones">
                <button v-for="z in moveTabs" :key="z.id" type="button" class="rt-move-zone"
                        :class="{ 'rt-move-zone-on': z.id === moveZoneId }" @click="moveZoneId = z.id">
                    {{ z.name }}
                </button>
            </div>
            <FloorMap v-if="moveZoneTables.length" :tables="moveZoneTables" :canvas="mapCanvas" compact
                      :disabledIds="[tableId]" @select="moveTo" />
            <div v-else class="rt-empty-sm">No hay mesas en esta zona.</div>
            <p class="rt-move-hint">Toca la mesa destino. Si ya tiene cuentas, esta se suma como una cuenta más.</p>
        </Dialog>

        <!-- Cambiar el nombre de la cuenta («Cuenta 2» → «Grupo Ricardo»), como en el teléfono -->
        <Dialog v-model:visible="renameVisible" :modal="true" header="Nombre de la cuenta" :style="{width: '380px'}"
                :breakpoints="{'500px': '96vw'}">
            <form @submit.prevent="saveRename">
                <InputText v-model="renameLabel" maxlength="60" class="w-full" aria-label="Nombre de la cuenta"
                           :class="{ 'p-invalid': renameError }" autofocus />
                <small v-if="renameError" class="p-error block mt-1">{{ renameError }}</small>
                <div class="flex justify-content-end gap-2 mt-3">
                    <Button type="button" label="Cancelar" class="p-button-text" @click="renameVisible = false" />
                    <Button type="submit" label="Guardar" icon="pi pi-check" :loading="renaming"
                            :disabled="renaming || !renameLabel.trim()" />
                </div>
            </form>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import FloorMap from './FloorMap.vue';
import PaymentEntryForm from './PaymentEntryForm.vue';
import InvoiceBuyerForm from './InvoiceBuyerForm.vue';
import PayerList from './PayerList.vue';
import { NO_ZONE, canvasOf, tablesForZone, zoneTabs } from '../../service/floorLayout';
import { emptyBuyer, buyerError, buyerPayload, DEFAULT_FINAL_CONSUMER_LIMIT } from '../../service/restaurantPayment';

const STATE_LABELS = { free: 'Libre', occupied: 'Ocupada', billing: 'Por cobrar', reserved: 'Reservada' };
// Campos del comprador que el servidor puede marcar al facturar (fiscal.buyerError / tope $50).
const BUYER_FIELDS = ['customer', 'name', 'identification', 'email'];
const DEFAULT_SETUP = { accounts: [], requiresAccount: false, requiresProof: false, canCollect: true,
                        consumidorFinalLimit: String(DEFAULT_FINAL_CONSUMER_LIMIT) };

export default {
    name: 'TableDetailPanel',
    components: { FloorMap, PaymentEntryForm, InvoiceBuyerForm, PayerList },
    props: {
        tableId: { type: Number, default: null },
        tables: { type: Array, default: () => [] },   // para el selector de «Mover» (mapa)
        zones: { type: Array, default: () => [] },
        canvas: { type: Object, default: null },
        // Perfil de la pantalla (Capa 4): `defaults.sendToKitchen` y `defaults.splitPayment`.
        kitchenEnabled: { type: Boolean, default: false },
        splitPayment: { type: Boolean, default: true },
    },
    emits: ['changed', 'open-account', 'add-items', 'close'],
    data() {
        return {
            detail: null,
            selectedAccountId: null,
            loading: false,
            payMode: null,           // null = elegir · 'full' = cobrar de una vez · 'split' = dividir
            paying: false,
            payError: null,          // {field} que devolvió el servidor al registrar un pago
            buyers: {},              // datos de factura por cuenta (sobreviven a recargas y pasos)
            buyerErrorField: null,
            collecting: false,
            kitchenBusy: false,
            moveVisible: false,
            renameVisible: false,
            renameLabel: '',
            renameError: null,
            renaming: false,
            moveZoneId: null,
        };
    },
    computed: {
        table() { return this.detail ? this.detail.table : {}; },
        accounts() { return this.detail ? this.detail.accounts : []; },
        account() {
            return this.accounts.find(a => a.id === this.selectedAccountId) || this.accounts[0] || null;
        },
        // Reglas del cobro decididas por el servidor (cuentas, cuenta/comprobante obligatorios, permiso).
        setup() { return (this.detail && this.detail.paymentSetup) || DEFAULT_SETUP; },
        finalLimit() { return Number(this.setup.consumidorFinalLimit || DEFAULT_FINAL_CONSUMER_LIMIT); },
        // Datos de factura de la cuenta abierta: consumidor final si se puede, si no, con datos.
        buyer: {
            get() {
                if (!this.account) return emptyBuyer(0, this.finalLimit);
                return this.buyers[this.account.id] || emptyBuyer(Number(this.account.total), this.finalLimit);
            },
            set(value) {
                if (!this.account) return;
                this.buyers = { ...this.buyers, [this.account.id]: value };
                this.buyerErrorField = null;
            },
        },
        // Etapa del cobro. Una cuenta con abonos ya está «dividida» aunque se recargue el panel.
        payStage() {
            if (!this.account) return 'choose';
            if (Number(this.account.total) > 0 && Number(this.account.pending) <= 0) return 'done';
            if (this.account.payments.length || this.payMode === 'split') return 'split';
            if (this.payMode === 'full') return 'full';
            return 'choose';
        },
        paidPercent() {
            if (!this.account || !Number(this.account.total)) return 0;
            return Math.min(100, Math.round((Number(this.account.paid) / Number(this.account.total)) * 100));
        },
        accountId() { return this.account ? this.account.id : null; },
        mapCanvas() { return this.canvas || canvasOf(null); },
        moveTabs() { return zoneTabs(this.zones, this.tables); },
        moveZoneTables() { return tablesForZone(this.tables, this.moveZoneId); },
        moveHeader() {
            return this.account ? `Mover «${this.account.accountLabel}» a otra mesa` : 'Mover cuenta a otra mesa';
        },
    },
    watch: {
        tableId: {
            immediate: true,
            handler(id) { if (id) this.load(); else this.detail = null; },
        },
        moveVisible(open) {
            // Abre en la zona de la mesa actual (lo usual: mover a la mesa de al lado).
            if (!open) return;
            const ids = this.moveTabs.map(z => z.id);
            const own = this.table.zoneId || NO_ZONE;
            this.moveZoneId = ids.includes(own) ? own : (ids[0] || null);
        },
        accountId() {
            // Otra cuenta (o mesa): vuelve a la pregunta «¿cómo cobrar?».
            this.payMode = null;
            this.payError = null;
            this.buyerErrorField = null;
        },
    },
    methods: {
        money(value) { return Number(value || 0).toFixed(2); },
        stateLabel(state) { return STATE_LABELS[state] || state; },
        async load(keepSelection = true) {
            if (!this.tableId) return;
            this.loading = true;
            const prev = this.selectedAccountId;
            const res = await httpRequest.getRestaurantTableDetail(this.tableId);
            this.loading = false;
            if (res && res.status === 200) {
                this.detail = res.data;
                const ids = this.accounts.map(a => a.id);
                if (!keepSelection || !ids.includes(prev)) {
                    this.selectedAccountId = ids.length ? ids[0] : null;
                }
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo cargar la mesa', life: 4000 });
            }
        },
        // Lo llama el padre tras abrir cuenta / agregar ítems, con el id de la cuenta a enfocar.
        async reload(focusAccountId = null) {
            await this.load(true);
            if (focusAccountId && this.accounts.some(a => a.id === focusAccountId)) {
                this.selectedAccountId = focusAccountId;
            }
            this.$emit('changed');
        },
        async removeItem(item) {
            const res = await httpRequest.removeRestaurantItem(this.account.id, item.id);
            if (res && res.status === 200) {
                await this.reload(this.account.id);
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo quitar el ítem',
                                  detail: (res && res.error) || '', life: 4000 });
            }
        },
        // ¿Se puede facturar con estos datos? Marca el campo y avisa si no (el servidor valida igual).
        checkBuyer() {
            const error = buyerError(this.buyer, Number(this.account.total), this.finalLimit);
            if (!error) return true;
            this.buyerErrorField = error.field;
            if (error.field === 'customer' && this.buyer.mode !== 'data') this.buyer = { ...this.buyer, mode: 'data' };
            this.$toast.add({ severity: 'warn', summary: 'Datos de factura', detail: error.message, life: 6000 });
            return false;
        },
        // Un pago. El parcial de una persona al dividir es un abono y la cuenta sigue abierta; el que
        // cobra lo que falta («Cobrar», o el último del pago múltiple) cobra, cierra y factura en un solo
        // paso, como el resto de Contablus.
        async onPaymentSubmit(p, intent) {
            if (intent !== 'abono') {
                await this.collect(intent === 'invoice' ? 'invoice' : 'archive', {
                    amount: p.amount.toFixed(2), paymentMethod: p.method, label: p.label, accountId: p.accountId,
                    approvalNumber: p.approvalNumber || null, proof: p.proof, tip: p.tip > 0 ? p.tip.toFixed(2) : null,
                });
                return;
            }
            const accountId = this.account.id;
            this.paying = true;
            this.payError = null;
            const res = await httpRequest.registerRestaurantAbono({
                saleId: accountId, amount: p.amount.toFixed(2), paymentMethod: p.method, label: p.label,
                accountId: p.accountId, approvalNumber: p.approvalNumber || null, proof: p.proof,
                tip: p.tip > 0 ? p.tip.toFixed(2) : null,
            });
            this.paying = false;
            if (!res || res.status !== 200) {
                this.payError = res && res.data && res.data.field ? { field: res.data.field } : null;
                this.$toast.add({ severity: 'warn', summary: 'No se registró el pago',
                                  detail: (res && res.error) || 'Intenta de nuevo.', life: 6000 });
                return;
            }
            const who = p.label ? ` de ${p.label}` : '';
            this.$toast.add({ severity: 'success', summary: `Pago${who} registrado`, life: 2000 });
            await this.reload(accountId);
            if (this.$refs.payForm) this.$refs.payForm.reset();
        },
        // Archivar = cobrar y no facturar: la cuenta queda COBRADA y archivada (el estándar de Contablus).
        confirmArchive() {
            this.$confirm.require({
                message: `«${this.account.accountLabel}» se archiva como cobrada.`,
                header: `¿Archivar ${this.account.accountLabel}?`, icon: 'pi pi-inbox',
                acceptLabel: 'Archivar', rejectLabel: 'Cancelar',
                accept: () => this.collect('archive'),
            });
        },
        // Cobra lo que falta (`payment`) y factura o archiva la cuenta. Sin `payment`: una
        // cuenta que ya estaba cubierta. Si el cierre se rechaza, no se cobró nada y la cuenta sigue aquí.
        async collect(mode, payment = null) {
            if (mode === 'invoice' && !this.checkBuyer()) return;
            const accountId = this.account.id;
            const buyer = mode === 'invoice' ? buyerPayload(this.buyer) : null;
            this.collecting = true;
            this.paying = !!payment;
            this.payError = null;
            const res = await httpRequest.collectRestaurantAccount(accountId, mode, buyer, payment);
            this.collecting = false;
            this.paying = false;
            const data = (res && res.data) || {};
            if (!data.closed) {
                // No se cerró (firma, comprador, la cuenta cambió…): no se cobró nada. Se corrige y se reintenta.
                const field = data.field;
                if (BUYER_FIELDS.includes(field)) this.buyerErrorField = field;
                else if (field) this.payError = { field };
                this.$toast.add({ severity: 'warn', life: 8000,
                                  summary: mode === 'invoice' ? 'No se pudo facturar' : 'No se pudo archivar',
                                  detail: (res && res.error) || 'Intenta de nuevo.' });
                await this.reload(accountId);
                return;
            }
            if (data.collected === false) {
                // Se cerró, pero el pago no entró: queda por cobrar en Cobros y pagos, como cualquier venta.
                this.$toast.add({ severity: 'warn', summary: 'La cuenta se cerró sin cobrar', life: 10000,
                                  detail: data.collectionError || 'Cóbrala desde Cobros y pagos.' });
            } else if (res.status === 200) {
                const sentTo = buyer && buyer.email ? `Enviada a ${buyer.email}.` : '';
                this.$toast.add({ severity: 'success', life: 3500, detail: sentTo,
                                  summary: mode === 'invoice' ? 'Cobrada y facturada' : 'Cobrada y archivada' });
            } else {
                // Cobrada y cerrada, pero el comprobante no salió (p.ej. 802): se reintenta desde Ventas.
                this.$toast.add({ severity: 'warn', summary: 'Cuenta cobrada, pero la factura no salió',
                                  detail: (res.error || 'Reintenta la factura desde Ventas.'), life: 8000 });
            }
            const buyers = { ...this.buyers };
            delete buyers[accountId];
            this.buyers = buyers;
            this.payMode = null;
            await this.reload();
        },
        // Reimprime la comanda de lo que la cuenta tiene sin enviar (lo agregado con la impresora
        // apagada); lo demás ya salió al agregarse. 701 = no hay impresora conectada.
        async sendToKitchen() {
            this.kitchenBusy = true;
            const res = await httpRequest.sendRestaurantToKitchen(this.account.id);
            this.kitchenBusy = false;
            if (res && res.status === 200) {
                const printed = (res.data && res.data.printed) || 0;
                if (printed) {
                    this.$toast.add({ severity: 'success', summary: 'Comanda enviada a cocina',
                                      detail: printed === 1 ? '1 ítem' : printed + ' ítems', life: 2500 });
                } else {
                    this.$toast.add({ severity: 'info', summary: 'Todo ya está en cocina', life: 2500 });
                }
                await this.reload();
            } else if (res && res.status === 701) {
                this.$toast.add({ severity: 'warn', summary: 'No hay una impresora conectada en el local', life: 4000 });
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo enviar a cocina',
                                  detail: (res && res.error) || '', life: 4000 });
            }
        },
        openRename() {
            this.renameLabel = this.account.accountLabel || '';
            this.renameError = null;
            this.renameVisible = true;
        },
        async saveRename() {
            const label = this.renameLabel.trim();
            if (!label || this.renaming) return;
            const accountId = this.account.id;
            this.renaming = true;
            const res = await httpRequest.renameRestaurantAccount(accountId, label);
            this.renaming = false;
            if (res && res.status === 200) {
                this.renameVisible = false;
                await this.reload(accountId);
                return;
            }
            this.renameError = (res && res.error) || 'No se pudo cambiar el nombre. Intenta de nuevo.';
        },
        async moveTo(target) {
            const res = await httpRequest.moveRestaurantAccount(this.account.id, target.id);
            this.moveVisible = false;
            if (res && res.status === 200) {
                this.$toast.add({ severity: 'success', summary: `Cuenta movida a ${target.name}`, life: 2500 });
                await this.reload();
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo mover la cuenta',
                                  detail: (res && res.error) || '', life: 4000 });
            }
        },
    },
};
</script>

<style scoped>
.rt-panel { min-height: 420px; display: flex; flex-direction: column; }
.rt-panel-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
.rt-panel-sub { color: var(--text-color-secondary); font-size: 0.85rem; }
.rt-state-chip { border-radius: 999px; padding: 0.25rem 0.75rem; font-size: 0.8rem; font-weight: 700; white-space: nowrap; }
.rt-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; background: var(--surface-100);
    border-radius: 10px; padding: 0.75rem 1rem; margin-bottom: 1rem; }
.rt-summary > div { display: flex; flex-direction: column; }
.rt-sum-label { color: var(--text-color-secondary); font-size: 0.75rem; }
.rt-sum-value { font-weight: 700; font-size: 1.1rem; }
.rt-pending { color: #d97706; }
.rt-accounts { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; }
.rt-acc-tab { border: 1px solid var(--surface-border); background: var(--surface-0); border-radius: 10px;
    padding: 0.4rem 0.75rem; cursor: pointer; display: flex; flex-direction: column; text-align: left; }
.rt-acc-active { border-color: var(--primary-color); background: var(--primary-color); color: var(--primary-color-text); }
.rt-acc-name { font-weight: 700; font-size: 0.9rem; }
.rt-acc-money { font-size: 0.75rem; opacity: 0.85; }
.rt-new-acc { align-self: center; }
.rt-items { border-top: 1px solid var(--surface-border); padding-top: 0.5rem; }
.rt-item-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0; }
.rt-item-qty { font-weight: 700; min-width: 1.5rem; }
.rt-item-name { flex: 1; }
.rt-item-total { font-weight: 600; }
.rt-item-del { color: var(--text-color-secondary) !important; }
.rt-empty-sm { color: var(--text-color-secondary); padding: 0.5rem 0; font-size: 0.9rem; }
.rt-totals { border-top: 1px solid var(--surface-border); margin-top: 0.5rem; padding-top: 0.5rem; }
.rt-totals > div { display: flex; justify-content: space-between; padding: 0.15rem 0; color: var(--text-color-secondary); }
.rt-grand { font-weight: 800; font-size: 1.1rem; color: var(--text-color) !important; }
.rt-actions { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; margin-top: 1rem; }
/* Cobro */
.rt-pay { margin-top: 1rem; border-top: 1px solid var(--surface-border); padding-top: 1rem;
    display: flex; flex-direction: column; gap: 0.6rem; }
.rt-pay-head { display: flex; justify-content: space-between; align-items: center; font-weight: 700; min-height: 2rem; }
.rt-pay-amount { font-size: 1.25rem; font-weight: 800; color: #d97706; }
.rt-pay-head.rt-pay-done .rt-pay-amount { color: #15803d; }
.rt-pay-head.rt-pay-done span:first-child { color: #15803d; display: inline-flex; align-items: center; gap: .35rem; }
.rt-pay-choices { display: flex; flex-direction: column; gap: 0.5rem; }
.rt-pay-choices :deep(.p-button) { width: 100%; justify-content: center; }
.rt-pay-primary { width: 100%; }
.rt-pay-secondary { align-self: center; }
.rt-pay-locked { display: flex; align-items: center; gap: .6rem; padding: .7rem .85rem; border-radius: 10px;
    background: var(--surface-100); color: var(--text-color-secondary); font-size: .9rem; }
.rt-pay-locked b { color: var(--text-color); }
.rt-progress { height: 8px; border-radius: 999px; background: var(--surface-200); overflow: hidden; }
.rt-progress span { display: block; height: 100%; background: #16a34a; border-radius: 999px; transition: width .3s; }
.rt-progress-legend { display: flex; justify-content: space-between; font-size: 0.82rem; color: var(--text-color-secondary); }
.rt-progress-legend b { color: #d97706; }
.rt-panel-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.5rem; color: var(--text-color-secondary); text-align: center; padding: 2rem 1rem; }
.rt-head-right { display: flex; align-items: center; gap: 0.25rem; }
.rt-move-zones { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; overflow-x: auto; }
.rt-move-zone { border: none; background: var(--surface-100); border-radius: 999px; padding: 0.35rem 0.9rem;
    cursor: pointer; font: inherit; font-weight: 600; color: var(--text-color-secondary); white-space: nowrap; }
.rt-move-zone-on { background: var(--primary-color); color: var(--primary-color-text); }
.rt-move-hint { color: var(--text-color-secondary); font-size: 0.85rem; margin: 0.75rem 0 0; }
.rt-state-free { background: #dcfce7; color: #15803d; }
.rt-state-occupied { background: #ffedd5; color: #c2410c; }
.rt-state-billing { background: #dbeafe; color: #1e3a8a; }
.rt-state-reserved { background: #e5e7eb; color: #4b5563; }
</style>
